/**
 * PDFViewer Component
 *
 * Displays PDF documents with page navigation, zoom controls, and text highlighting.
 * Uses react-pdf (PDF.js) for rendering with text layer support.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  Loader2, AlertCircle, X, Maximize2, Minimize2
} from 'lucide-react';

// Import react-pdf styles for text layer and annotations
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  /** PDF file as blob URL or base64 data URL */
  file: string | null;
  /** Initial page to display */
  initialPage?: number;
  /** Text to highlight in the document */
  highlightText?: string;
  /** Callback when viewer is closed */
  onClose?: () => void;
  /** Document title for display */
  title?: string;
  /** Whether to show in fullscreen mode */
  fullscreen?: boolean;
  /** Toggle fullscreen callback */
  onToggleFullscreen?: () => void;
}

export function PDFViewer({
  file,
  initialPage = 1,
  highlightText,
  onClose,
  title,
  fullscreen = false,
  onToggleFullscreen
}: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  // Reset page when file changes
  useEffect(() => {
    setCurrentPage(initialPage);
    setLoading(true);
    setError(null);
  }, [file, initialPage]);

  // Highlight text after page renders
  useEffect(() => {
    if (!highlightText || loading) return;

    // Small delay to ensure text layer is rendered
    const timer = setTimeout(() => {
      highlightTextInPage(highlightText);
    }, 500);

    return () => clearTimeout(timer);
  }, [highlightText, currentPage, loading]);

  const highlightTextInPage = useCallback((searchText: string) => {
    if (!pageRef.current || !searchText) return;

    // Remove existing highlights
    const existingHighlights = pageRef.current.querySelectorAll('.pdf-text-highlight');
    existingHighlights.forEach(el => el.classList.remove('pdf-text-highlight'));

    // Find text layer
    const textLayer = pageRef.current.querySelector('.react-pdf__Page__textContent');
    if (!textLayer) return;

    // Get all text spans
    const textSpans = Array.from(textLayer.querySelectorAll('span'));
    if (textSpans.length === 0) return;

    // Light normalization - preserves meaning but handles whitespace variations
    const normalizeLight = (t: string) => t
      .toLowerCase()
      .replace(/\s+/g, ' ')  // Collapse whitespace
      .replace(/[\u2018\u2019]/g, "'")  // Smart quotes to regular
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')  // En/em dashes to hyphen
      .trim();

    // Heavy normalization - for fuzzy matching (removes punctuation)
    const normalizeHeavy = (t: string) => normalizeLight(t)
      .replace(/[^\w\s]/g, ' ')  // Punctuation to spaces
      .replace(/\s+/g, ' ')
      .trim();

    const searchNormLight = normalizeLight(searchText);
    const searchNormHeavy = normalizeHeavy(searchText);

    // Build page text with both normalizations and track span positions
    const spanData: { span: Element; startLight: number; endLight: number; startHeavy: number; endHeavy: number }[] = [];
    let fullTextLight = '';
    let fullTextHeavy = '';

    textSpans.forEach((span) => {
      const spanText = span.textContent || '';
      if (!spanText.trim()) return;

      const normLight = normalizeLight(spanText);
      const normHeavy = normalizeHeavy(spanText);

      if (normLight.length > 0) {
        const startLight = fullTextLight.length;
        const startHeavy = fullTextHeavy.length;

        fullTextLight += (fullTextLight ? ' ' : '') + normLight;
        fullTextHeavy += (fullTextHeavy ? ' ' : '') + normHeavy;

        spanData.push({
          span,
          startLight,
          endLight: fullTextLight.length,
          startHeavy,
          endHeavy: fullTextHeavy.length,
        });
      }
    });

    // STRATEGY 1: Try exact match with light normalization (preserves punctuation)
    let matchStart = fullTextLight.indexOf(searchNormLight);
    let matchEnd = matchStart !== -1 ? matchStart + searchNormLight.length : -1;
    let useHeavy = false;

    // STRATEGY 2: Try exact match with heavy normalization (ignores punctuation)
    if (matchStart === -1) {
      matchStart = fullTextHeavy.indexOf(searchNormHeavy);
      matchEnd = matchStart !== -1 ? matchStart + searchNormHeavy.length : -1;
      useHeavy = true;
    }

    // STRATEGY 3: Try matching significant substrings (first/last 60 chars)
    if (matchStart === -1 && searchNormHeavy.length > 80) {
      // Try first portion
      const firstPart = searchNormHeavy.slice(0, 60);
      matchStart = fullTextHeavy.indexOf(firstPart);
      if (matchStart !== -1) {
        // Found start, now find how far it extends
        matchEnd = matchStart + Math.min(searchNormHeavy.length, 200);
        useHeavy = true;
      }

      // Try last portion if first didn't work
      if (matchStart === -1) {
        const lastPart = searchNormHeavy.slice(-60);
        const lastIdx = fullTextHeavy.indexOf(lastPart);
        if (lastIdx !== -1) {
          matchStart = Math.max(0, lastIdx - (searchNormHeavy.length - 60));
          matchEnd = lastIdx + lastPart.length;
          useHeavy = true;
        }
      }
    }

    // STRATEGY 4: Find consecutive distinctive words (3+ words of 5+ chars)
    if (matchStart === -1) {
      const words = searchNormHeavy.split(' ').filter(w => w.length >= 5);

      // Try to find a sequence of 3+ consecutive distinctive words
      for (let windowSize = Math.min(words.length, 5); windowSize >= 3; windowSize--) {
        for (let i = 0; i <= words.length - windowSize; i++) {
          const phrase = words.slice(i, i + windowSize).join(' ');
          const idx = fullTextHeavy.indexOf(phrase);
          if (idx !== -1) {
            matchStart = idx;
            matchEnd = idx + phrase.length;
            useHeavy = true;
            break;
          }
        }
        if (matchStart !== -1) break;
      }
    }

    // Highlight matching spans
    if (matchStart !== -1) {
      let firstMatch: Element | null = null;
      const startKey = useHeavy ? 'startHeavy' : 'startLight';
      const endKey = useHeavy ? 'endHeavy' : 'endLight';

      spanData.forEach((data) => {
        const spanStart = data[startKey];
        const spanEnd = data[endKey];

        // Check if this span overlaps with our match range
        if (spanStart < matchEnd && spanEnd > matchStart) {
          data.span.classList.add('pdf-text-highlight');
          if (!firstMatch) {
            firstMatch = data.span;
          }
        }
      });

      // Scroll to first highlighted span
      if (firstMatch) {
        (firstMatch as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (err: Error) => {
    console.error('PDF load error:', err);
    setError('Failed to load PDF document');
    setLoading(false);
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= numPages) {
      setCurrentPage(page);
    }
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));

  if (!file) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-500 dark:text-gray-400">No document selected</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full bg-gray-100 dark:bg-gray-900 ${fullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200 flex-shrink-0 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center gap-2 min-w-0">
          {title && (
            <h3 className="font-medium text-gray-900 truncate text-sm dark:text-gray-100">{title}</h3>
          )}
        </div>
        <div className="flex items-center gap-1">
          {onToggleFullscreen && (
            <button
              onClick={onToggleFullscreen}
              className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
              title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
              title="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200 flex-shrink-0 dark:bg-gray-800 dark:border-gray-700">
        {/* Page navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm text-gray-600 min-w-[80px] text-center dark:text-gray-300">
            {loading ? '...' : `${currentPage} / ${numPages}`}
          </span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= numPages}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            disabled={scale <= 0.5}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ZoomOut size={18} />
          </button>
          <span className="text-sm text-gray-600 min-w-[50px] text-center dark:text-gray-300">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={scale >= 3}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ZoomIn size={18} />
          </button>
        </div>
      </div>

      {/* PDF Content */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto p-4 flex justify-center"
      >
        {error ? (
          <div className="flex flex-col items-center justify-center text-red-600 gap-2 dark:text-red-400">
            <AlertCircle size={32} />
            <p>{error}</p>
          </div>
        ) : (
          <div ref={pageRef} className="pdf-page-container">
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <Loader2 className="animate-spin" size={20} />
                  <span>Loading PDF...</span>
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                loading={
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Loader2 className="animate-spin" size={20} />
                    <span>Loading page...</span>
                  </div>
                }
              />
            </Document>
          </div>
        )}
      </div>

      {/* Highlight text indicator */}
      {highlightText && !loading && (
        <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200 flex-shrink-0 dark:bg-yellow-900/20 dark:border-yellow-800">
          <p className="text-xs text-yellow-800 truncate dark:text-yellow-300">
            Highlighting: "{highlightText.slice(0, 100)}{highlightText.length > 100 ? '...' : ''}"
          </p>
        </div>
      )}

      {/* CSS for highlighting */}
      <style>{`
        .pdf-text-highlight {
          background-color: rgba(250, 204, 21, 0.7) !important;
          border-radius: 2px;
          padding: 2px 1px;
          box-decoration-break: clone;
        }
        .pdf-page-container {
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          background: white;
        }
        .react-pdf__Page__textContent {
          user-select: text;
        }
      `}</style>
    </div>
  );
}

export default PDFViewer;
